import React, {useCallback, useEffect, useState} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useWindowSize} from '../../../../util/formatter';
import API from '../../../../app/api';
import _ from 'lodash';
import moment from 'moment';
import clsx from 'clsx';
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import div from '@mui/material/Paper';
import MessageBox from '../../../../util/messageBox';
import DialogContent from '@mui/material/DialogContent';
import {addNoteForPatient} from '../../../../store/reducers/notes';
import 'chartjs-adapter-moment';
import BlockIcon from '@mui/icons-material/Block';
import SnoozeIcon from '@mui/icons-material/Snooze';
import RestoreIcon from '@mui/icons-material/Restore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {hasPermission, Permissions} from '../../../../store/reducers/user';
import {TextFieldDebounce} from '../../../../controls/agyle/textFieldDebounce';
import Notes from '../../../../util/notes';
import {ObsSuspendReason} from '../../../../controls/agyle/dictionaries';
import ObservationChart from './charts/observationChart';
import {setSnackbar} from '../../../../store/reducers/globals';
import {getFullObsName, getObsDisplayValues, mapObservationFromAPI, obsStatuses, obsTypes} from './obsUtils';
import Typography from '@mui/material/Typography';
import AgyleDialog from '../../../../components/modal/agyleDialog';
import ObservationTable from './components/observationTable';
import {TimerCountUp} from '../../../../controls/agyle/timer';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import {isSpecialSmallDeviceDetect} from '../../../../util/isSpecialSmallDeviceDetect';
import {getConfig} from '../../../../util/configUtil';
import {
    ctxObservationMethod,
    ctxObservationType,
    ctxObservationViewType,
    ctxObsSettings,
    INIT_PREV_OBS_STATS,
    openPatientForm,
    setObservationMethod,
    setPrevObsStats,
} from '../../../../store/reducers/patientContext';
import {GLOBAL_PANEL_ACTION_TYPE} from '../../../../components/actionPanel/components/panelType';
import {setActionPanelDisableComponent} from '../../../../store/reducers/globalModal';
import {obsStyles} from './obsStyle';
import {PATIENT_AGE_TYPE} from '../../../../util/patientContextUtil';

const useChartStyles = (displayValues) =>
    makeStyles((theme) => {
        const classes = {};
        _.forEach(displayValues, (item) => {
            if (item.chart?.enabled) {
                classes['obsLabelCellSelected_' + item.field] = {
                    border: '2px solid ' + item.chart.dataset.backgroundColor,
                    background: item.chart.dataset.borderColor,
                };
            }
        });

        return classes;
    });

function Observations(props) {
    const dispatch = useDispatch();
    const classes = obsStyles();
    const {t} = useTranslation();
    const isSmallDevice = isSpecialSmallDeviceDetect();

    // Selectors
    const obsSettings = useSelector(ctxObsSettings);
    const obsType = useSelector(ctxObservationType);
    const obsViewType = useSelector(ctxObservationViewType);
    const nextObsMethod = useSelector(ctxObservationMethod);

    // Configs
    const nonContactObsAvailable = getConfig('nonContactObsAvailable', false);

    const [showInProgress, setShowInProgress] = React.useState(false);
    const [viewMode, setViewMode] = React.useState('table');

    const [observations, setObservations] = React.useState([]);
    const [loadingObs, setLoadingObs] = useState(false);
    const [chartData, setChartData] = React.useState(null);
    const [chartOptions, setChartOptions] = React.useState(null);
    const [actionButtons, setActionButtons] = React.useState([]);

    const [windowWidth, windowHeight] = useWindowSize();
    const [chartSize, setChartSize] = React.useState({
        width: 0,
        height: 0,
    });

    const [obs, setObs] = React.useState(null);
    const [confirmCancel, setConfirmCancel] = React.useState(false);
    const [showDefSusCan, setShowDefSusCan] = React.useState({
        open: false,
        type: null,
        lastObs: null,
    });

    // AGL-730 - Switch NEWS/PEWS/Neurological Observations categories
    const [obsMode, setObsMode] = React.useState('obs'); // ["obs", "neuro"]

    const controller = new AbortController();
    const obsDisplayValues = getObsDisplayValues(obsMode === 'obs' ? obsSettings.obsType : obsTypes.neuro);
    const chartClasses = useChartStyles(obsDisplayValues)();

    const [chartTimeAxis, setChartTimeAxis] = React.useState('time');
    const [chartSelectedValues, setChartSelectedValues] = React.useState({});
    const [chartSelectedCount, setChartSelectedCount] = React.useState(0);

    // NextObs change status reason
    const [reason, setReason] = useState(null);
    const [closeBanner, setCloseBanner] = useState(false);

    useEffect(() => {
        const chartValues = {};
        let count = 0;
        _.forEach(obsDisplayValues, (item) => {
            if (item.chart?.enabled && (!item.obsTypes || _.includes(item.obsTypes, obsMode === 'obs' ? obsSettings.obsType : obsTypes.neuro))) {
                chartValues[item.field] = true;
                count++;
            }
        });
        setChartSelectedCount(count);
        setChartSelectedValues(chartValues);
    }, [viewMode]);

    /**
     * Effect hook that opens the non-contact observation form if the observation type is 'nonContact'.
     * Otherwise, it closes the non-contact observation form and sets the observation mode.
     *
     * @param {string} obsType - The type of observation.
     */
    useEffect(() => {
        if (obsType === 'nonContact') {
            dispatch(openPatientForm('aeAssessment'))
        } else {
            // closeNonContact();
            setObsMode(obsType);
        }
    }, [obsType]);

    /**
     * Updates the view mode whenever the observation view type changes.
     */
    useEffect(() => {
        setViewMode(obsViewType);
    }, [obsViewType]);

    /**
     * Returns the last observation that is not cancelled or partial.
     *
     * @returns {Object|null} The last valid observation or null if none found.
     */
    const prevObservation = useCallback(() => {
        return _.findLast(observations, (item) => {
            return (
                item.status !== obsStatuses.cancelled &&
                item.status !== obsStatuses.partial
            );
        });
    }, [observations]);

    /**
     * Effect hook that handles the next observation method.
     * It checks the next observation method and performs the corresponding action
     * (cancel, defer, suspend, or unpause) based on the permissions and the current observation status.
     */
    useEffect(() => {
        if (nextObsMethod) {
            const prev = prevObservation();

            // eslint-disable-next-line default-case
            switch (nextObsMethod) {
                case GLOBAL_PANEL_ACTION_TYPE.CANCEL_OBSERVATION:
                    if (!prev || !dispatch(hasPermission(Permissions.AGYLE_CAN_EDIT_FREQUENCY_CANCEL_OBSERVATIONS))
                        || prev?.nextObsStatus === obsStatuses.cancelled || obsMode !== 'obs') {
                        dispatch(setSnackbar({type: 'error', message: t('Cannot cancel next observation')}));
                    } else {
                        openDefSusCan('CancelNextObs');
                    }
                    break;
                case GLOBAL_PANEL_ACTION_TYPE.DEFER_OBSERVATION:
                    if (!prev || !dispatch(hasPermission(Permissions.AGYLE_CAN_EDIT_FREQUENCY_CANCEL_OBSERVATIONS))
                        || prev?.nextObsStatus === obsStatuses.deferred || prev?.nextObsStatus === obsStatuses.cancelled || obsMode !== 'obs') {
                        dispatch(setSnackbar({type: 'error', message: t('Cannot defer next observation')}));
                    } else {
                        openDefSusCan('DeferNextObs');
                    }
                    break;
                case GLOBAL_PANEL_ACTION_TYPE.SUSPEND_OBSERVATION:
                    // prevObservation()?.nextObsStatus !== obsStatuses.suspended
                    if (!prev || !dispatch(hasPermission(Permissions.AGYLE_CAN_EDIT_FREQUENCY_CANCEL_OBSERVATIONS))
                        || prev?.nextObsStatus === obsStatuses.suspended || prev?.nextObsStatus === obsStatuses.cancelled || obsMode !== 'obs') {
                        dispatch(setSnackbar({type: 'error', message: t('Cannot suspend next observation')}));
                    } else {
                        openDefSusCan('SuspendNextObs');
                    }
                    break;
                case GLOBAL_PANEL_ACTION_TYPE.UNPAUSE_OBSERVATION:
                    // prevObservation()?.nextObsStatus === obsStatuses.suspended
                    if (!prev || !dispatch(hasPermission(Permissions.AGYLE_CAN_EDIT_FREQUENCY_CANCEL_OBSERVATIONS))
                        || prev?.nextObsStatus === obsStatuses.unpaused || prev?.nextObsStatus === obsStatuses.cancelled || obsMode !== 'obs') {
                        dispatch(setSnackbar({type: 'error', message: t('Cannot unpause next observation')}));
                    } else {
                        openDefSusCan('UnpausedNextObs');
                    }
                    break;
            }
            dispatch(setObservationMethod(null));
        }
    }, [nextObsMethod, observations, obsMode]);

    /**
     * Effect hook that disables the action panel component based on the status of the previous observation.
     * If the previous observation's next status is suspended, it disables the suspend observation action.
     * Otherwise, it disables the unpause observation action.
     */
    useEffect(() => {
        const id = prevObservation()?.nextObsStatus === obsStatuses.suspended
            ? GLOBAL_PANEL_ACTION_TYPE.SUSPEND_OBSERVATION : GLOBAL_PANEL_ACTION_TYPE.UNPAUSE_OBSERVATION;
        dispatch(setActionPanelDisableComponent([id]));
    }, [observations]);

    const refreshObservation = (newType) => {
        setLoadingObs(true);
        API.getFullObservations(props.patient, newType, false, controller.signal)
            .then((result) => {
                setShowInProgress(false);

                let obs = [];
                _.forEach(result, (apiObs) => {
                    obs.push(mapObservationFromAPI(apiObs, true));
                });

                setObservations(obs);
                setCloseBanner(false);
            })
            .catch((error) => {
                setShowInProgress(false);
                console.error('Observations.API.getObservations: ', error);
            })
            .finally(() => {
                setLoadingObs(false);
            });
    };

    useEffect(() => {
        if (props.patient) {
            setShowInProgress(true);
            setObservations([]);
            refreshObservation(obsMode === 'obs' ? obsSettings.obsType : obsTypes.neuro);
        }
    }, [props.patient, obsMode]);

    useEffect(() => {
        // TODO: calc the proper size of a chart
        setChartSize({
            width: windowWidth * 0.8,
            height: windowHeight - 200,
        });
    }, [windowWidth, windowHeight]);

    useEffect(() => {
        const options = {
            scales: {
                // 'y-axis-1': {
                //     type: 'linear',
                //     display: true,
                //     position: 'left',
                //
                //     title: {
                //         display: true,
                //         text: "Some Y axis 1",
                //         align: "start",
                //     },
                // },

                x: {
                    // type: 'timeseries',
                    // type: 'time',
                    type: chartTimeAxis,
                },
            },
        };

        const chart = {
            labels: [],
            datasets: [],
        };

        _.forEach(obsDisplayValues, (nValue) => {
            if (nValue.type !== 'button' && nValue.chart?.enabled && nValue.field && nValue.field in chartSelectedValues
                && (!nValue.obsTypes || _.includes(nValue.obsTypes, obsMode === 'obs' ? obsSettings.obsType : obsTypes.neuro))) {
                const dataset = {
                    ...nValue.chart.dataset,
                    label: nValue.description,
                    data: [],
                    yAxisID: nValue.field,
                };
                chart.datasets.push(dataset);

                const yAxis = {
                    type: 'linear',
                    display: true,
                    position: chart.datasets.length % 2 ? 'left' : 'right',
                    // backgroundColor: "red",

                    title: {
                        display: true,
                        align: 'end',
                        text: nValue.description,
                        // color: "red",
                        // font
                        // padding
                    },
                };
                _.merge(yAxis, nValue.chart.yAxis);
                options.scales[nValue.field] = yAxis;

                _.forEach(observations, (obs) => {
                    if (obs.status !== 'cancelled') {
                        // dataset.data.push(printValue(obs, nValue));
                        dataset.data.push({
                            x: obs.completedDate,
                            y: _.get(obs, nValue.field),
                        });
                    }
                });
            }
        });

        // _.forEach(observations, obs => {
        //     if (obs.status !== "cancelled") {
        //         chart.labels.push(obs.completedDate);
        //     }
        // });

        setChartData(chart);
        setChartOptions(options);
    }, [observations, chartSelectedValues, chartTimeAxis]);

    const openDefSusCan = (type) => {
        let prev = prevObservation();

        if (prev) {
            if (prev.nextObsStatus && prev.nextObsStatus === obsStatuses.cancelled) {
                dispatch(setSnackbar({
                    type: 'warning',
                    message: 'Next observations were canceled. Start a new observation.',
                }));
                setShowDefSusCan({open: false, type: null, lastObs: null});
            } else if (type === 'DeferNextObs') {
                const dif = moment
                    .duration(moment(prev.nextObsDate).diff(moment()))
                    .asMinutes();
                if (dif > 30) {
                    dispatch(setSnackbar({
                        type: 'warning',
                        message: 'To defer the next observation, there must be at least 30 minutes before the end of time.',
                    }));
                    setShowDefSusCan({
                        open: false,
                        type: null,
                        lastObs: null,
                    });
                } else {
                    setShowDefSusCan({
                        open: true,
                        type: type,
                        lastObs: prev,
                    });
                }
            } else {
                setShowDefSusCan({open: true, type: type, lastObs: prev});
            }
        }
    };

    const createObs = () => {
        let prev = prevObservation();
        if (prev) {
            dispatch(setPrevObsStats({
                totalScore: prev.totalScore,
                riskScore: prev.riskLevel,
                obsDate: prev.nextObsDate,
            }))
        } else {
            dispatch(setPrevObsStats(INIT_PREV_OBS_STATS))
        }
        setObs(null);
        dispatch(openPatientForm(obsMode !== 'neuroObs' ? 'obs': obsMode));
    };

    useEffect(() => {
        let prev = prevObservation();
        let buttons = [];

        // Add button when there is obs and is not a triage element
        if (prev && !props.triage && obsMode !== obsTypes.neuro) {
            buttons = [
                {
                    name: 'CancelNextObs',
                    display: t('Cancel next obs'),
                    icon: <BlockIcon/>,
                    color: 'warning',
                    event: () => openDefSusCan('CancelNextObs'),
                    disabled: !dispatch(hasPermission(Permissions.AGYLE_CAN_EDIT_FREQUENCY_CANCEL_OBSERVATIONS)),
                },
                {
                    name: 'DeferNextObs',
                    display: t('Defer next obs'),
                    icon: <RestoreIcon/>,
                    color: 'warning',
                    event: () => openDefSusCan('DeferNextObs'),
                    disabled: !dispatch(hasPermission(Permissions.AGYLE_CAN_EDIT_FREQUENCY_CANCEL_OBSERVATIONS)),
                },
            ];

            if (prev.nextObsStatus && prev.nextObsStatus === obsStatuses.suspended) {
                buttons.push({
                    name: 'UnpausedNextObs',
                    display: t('Unpause next obs'),
                    icon: <PlayArrowIcon/>,
                    color: 'warning',
                    event: () => openDefSusCan('UnpausedNextObs'),
                });
            } else {
                buttons.push({
                    name: 'SuspendNextObs',
                    display: t('Suspend next obs'),
                    icon: <SnoozeIcon/>,
                    color: 'warning',
                    event: () => openDefSusCan('SuspendNextObs'),
                    disabled: !dispatch(hasPermission(Permissions.AGYLE_CAN_EDIT_FREQUENCY_CANCEL_OBSERVATIONS)),
                });
            }
        }

        if (!props?.triage && nonContactObsAvailable && !props.ipod) {
            buttons.push(
                {
                    name: 'OpenNonContact',
                    display: t('Open Non-Contact obs'),
                    icon: <AddIcon/>,
                    event: () => dispatch(openPatientForm('aeAssessment')),
                    disabled: props?.triage || !dispatch(hasPermission(Permissions.AGYLE_CAN_ENTER_OBSERVATIONS)),
                });
        }

        // AGL-705 - 002. Add New Observations
        buttons.push({
            name: 'AddObs',
            display: obsSettings.obsType === obsTypes.news ? t('Add new NEWS obs') : obsSettings.obsType === obsTypes.pews ? t('Add new PEWS obs') : t('Add new Neurological obs'),
            icon: <AddIcon color="#2280f7"/>,
            event: createObs,
            disabled: !dispatch(hasPermission(Permissions.AGYLE_CAN_ENTER_OBSERVATIONS)),
        });

        if (!isSmallDevice) setActionButtons(buttons);
        if (props?.triage || props.actionButtonsOnBottomBar || isSmallDevice) props.registerTabActions(buttons);

        let timeoutId;
        // if (scrollRef.current) {
        //     scrollRef.current.scrollIntoView({
        //         behavior: 'smooth',
        //         inline: 'end',
        //     });
        //     timeoutId = setTimeout(() => {
        //         scrollTopRef.current.scrollIntoView({
        //             behavior: 'smooth',
        //             block: 'start',
        //         });
        //     }, 500);
        // }

        return () => {
            // clear ...
            // props.registerTabActions([]);
            clearTimeout(timeoutId);
        };
    }, [observations, obsSettings]);

    const saveCancelNotes = (obs) => {
        // build a note content
        const type = obs.type || obsTypes.news;
        const note = Notes.getCancelObservationNote(obs, type, getFullObsName(type));

        API.cancelClinicalNoteByRefId(props.patient, obs.__id)
            .catch((error) => {
                console.warn('Note for this observation was not found', obs.__id);
            })
            .finally(() => {
                dispatch(addNoteForPatient(props.patient.patient.__id, {
                    note: note,
                    noteType: 'cancelObs',
                    patientsInWardsRef: [props.patient.__id],
                    episodeId: props.patient?.admission?.consultantEpisodeId || null,
                }, props.patient.__id));
            });
    };

    const modifyLastObs = (response) => {
        if ((response.status === 'completed' || response.status === 'notRecorded')
            && (response.investigation === 'pews' || response.investigation === 'news')) {
            props.modyfyLastObs(response);
        }
    };

    const updateLastObs = () => {
        const doneObs = _.orderBy(
            _.filter(observations, (obs) => {
                return (
                    obs.status === 'completed' || obs.status === 'notRecorded'
                );
            }),
            ['completedDate'],
            ['desc'],
        );

        props.modyfyLastObs(!_.isEmpty(doneObs) ? doneObs[0] : null);
    };

    const onCancelObs = async () => {
        if (obs) {
            setShowInProgress(true);
            API.cancelObservation({
                    patientId: props.patient.patient.__id,
                    observationId: obs.__id,
                })
                .then((apiObs) => {
                    setShowInProgress(false);

                    let result = mapObservationFromAPI(apiObs, true);

                    obs.status = result.status;
                    obs.canceledDate = result.canceledDate
                        ? moment(result.canceledDate)
                        : null;
                    obs.canceledByFullName = result.canceledByFullName;
                    obs.canceledBy = result.canceledBy;

                    saveCancelNotes(result);

                    // Replace last observation in patient view
                    updateLastObs();

                    setObs(null);

                    API.updatePatientInWard(props.patient.__id, [{
                        'metadata.lastObsCanceled': {
                            obs_id: obs.__id,
                            date: moment().toISOString(),
                        },
                    }]);
                    dispatch(setSnackbar({type: 'success', message: 'Observation was successfully canceled.'}));
                })
                .catch((error) => {
                    setShowInProgress(false);
                    setObs(null);
                    console.error(
                        'Observations.API.cancelObservation: ',
                        error,
                    );
                    dispatch(setSnackbar({type: 'error', message: 'Observation was not successfully canceled.'}));
                });
        } else {
            // cancel a new one
            setObs(null);
        }
    };

    const onConfirmCancel = () => {
        setConfirmCancel(false);
        onCancelObs().then(() => {
        });
    };


    const getDisplayColorStyle = (item) => {
        if (viewMode === 'chart' && item.field && item.field in chartSelectedValues) {
            return chartClasses['obsLabelCellSelected_' + item.field];
        }
    };

    const onObsLabelClick = (item) => {
        const selected = {...chartSelectedValues};
        if (item.field in selected) {
            delete selected[item.field];
        } else {
            selected[item.field] = true;
        }
        setChartSelectedValues(selected);
        setChartSelectedCount(Object.keys(selected).length);
    };

    const createNoteForNextObsStatus = (nextObs) => {
        const note = Notes.getNextObservationStatusNote(nextObs);
        dispatch(addNoteForPatient(props.patient.patient.__id, {
            note: note,
            noteType: 'obs',
            patientsInWardsRef: [props.patient.__id],
            episodeId: props.patient?.admission?.consultantEpisodeId || null,
        }, props.patient.__id));
    };

    // Change status of next observation and refresh table
    const changeNextObsStatus = () => {
        if (showDefSusCan && showDefSusCan.type) {
            const dateOfForm = moment().toISOString();
            const last = showDefSusCan.lastObs;
            const status = showDefSusCan.type === 'CancelNextObs' ? obsStatuses.cancelled : showDefSusCan.type === 'SuspendNextObs'
                ? obsStatuses.suspended : showDefSusCan.type === 'DeferNextObs'
                    ? obsStatuses.deferred : showDefSusCan.type === 'UnpausedNextObs' ? obsStatuses.unpaused : null;

            let data = {
                nextObsDate: last.nextObsDate.toISOString(),
                nextObsStatus: status,
                nextObsReason: _.isString(reason)
                    ? reason
                    : reason.__description,
            };

            if (status === obsStatuses.deferred) {
                data = {
                    ...data,
                    nextObsDate: moment().add(1, 'h').toISOString(),
                };
            }

            API.changeNextObsStatus(props.patient.patient.__id, last.__id, data)
                .then((response) => {
                    let obs = mapObservationFromAPI(response, true);

                    // Replace last observation in patient view
                    modifyLastObs(response);

                    createNoteForNextObsStatus(data);
                    const admissionDate = moment(props.patient.admission.startDate);
                    const dob = moment(props.patient.patient.dob);
                    const age = admissionDate.diff(dob, 'years');
                    const newType = age >= 18 ? obsTypes.news : obsTypes.pews;
                    refreshObservation(newType);

                    // For refresh Last Observation
                    API.updatePatientInWard(props.patient.__id, [
                            {
                                'metadata.lastObs': {
                                    obs_id: obs.__id,
                                    date: dateOfForm,
                                },
                            },
                        ])
                        .catch((error) => {
                            console.error('Observations.API.updatePatientInWard: ', error);
                        });
                });
        }
        setReason('');
        setShowDefSusCan({open: false, type: null, lastObs: null});
    };

    const countdownLimitCatch = (e) => {
        if (e) setCloseBanner(true);
    };

    const renderNextObsStatus = () => {
        const prev = prevObservation();

        const getIcon = () => {
            // eslint-disable-next-line default-case
            switch (prev.nextObsStatus) {
                case obsStatuses.deferred:
                    return <RestoreIcon/>;
                case obsStatuses.suspended:
                    return <SnoozeIcon/>;
                case obsStatuses.cancelled:
                    return <BlockIcon/>;
            }
        };

        const getReason = () => {
            // eslint-disable-next-line default-case
            switch (prev.nextObsStatus) {
                case obsStatuses.deferred:
                    return (
                        <div className={classes.divRow}>
                            <span>{t('Next observation is deferred for')}&nbsp;</span>
                            <TimerCountUp
                                time={prev.nextObsDate}
                                withoutHours
                                limitMessage={'00:00'}
                                countDown
                                limit={0}
                                upperLimit={1}
                                catchLimit={countdownLimitCatch}
                            />
                            <span>&nbsp;{t('minutes. Reason')}: &nbsp;</span>
                            {prev.nextObsReason || '-'}
                        </div>
                    );
                case obsStatuses.suspended:
                    return (
                        <span>
                            {t('Next observation currently is suspended for this patient. Reason: ')}
                            {prev.nextObsReason || '-'}
                        </span>
                    );
                case obsStatuses.cancelled:
                    return (
                        <span>
                            {t('Next observation is cancelled for this patient. Reason: ')}
                            {prev.nextObsReason || '-'}
                        </span>
                    );
            }
        };

        if (prev && prev.nextObsStatus !== obsStatuses.new && prev.nextObsStatus !== obsStatuses.unpaused && !closeBanner) {
            return (
                <div className={classes.nextObsStatus}>
                    <div className={classes.nextObsIcon}>
                        {getIcon()}
                    </div>

                    <div className={classes.nextObsText}>
                        {getReason()}
                    </div>
                </div>
            );
        }
    };

    function renderObservations() {
        return (
            <div className={clsx(classes.obsResultsContent, 'scroll-normal-white')} id="obs-results-content">
                {viewMode === 'table' &&
                    <ObservationTable
                        observations={observations}
                        type={obsMode === 'obs' ? obsSettings.obsType : obsTypes.neuro}
                        createObs={createObs}
                        displayValues={obsDisplayValues}
                        disableSave={props.disableSave}
                        setObs={setObs}
                        setConfirmCancel={setConfirmCancel}
                        loadingObs={loadingObs}
                    />
                }

                {viewMode === 'chart' &&
                    <ObservationChart patient={props.patient} type={props.patientAgeType !== PATIENT_AGE_TYPE.PAEDS ? obsTypes.news : obsTypes.pews}/>
                }
            </div>
        );
    }

    const getObservationActionTitle = () => {
        let text;
        switch (showDefSusCan.type) {
            case 'DeferNextObs':
                text = 'Do you want to defer next observation?';
                break;
            case 'SuspendNextObs':
                text = 'Do you want to suspend next observation?';
                break;
            case 'CancelNextObs':
                text = 'Do you want to cancel next observation?';
                break;
            case 'UnpausedNextObs':
                text = 'Do you want to resume observation?';
                break;
            default:
                text = '';
        }
        return text || '';
    };

    return (
        <>
            {!props.ipod &&
                <div className={classes.actionBar}>
                    {renderNextObsStatus()}
                </div>
            }

            {props.ipod &&
                <>
                    {actionButtons.length > 0 && (
                        <Grid item
                              style={{display: 'flex', alignItems: 'center', overflowX: 'auto', overflowY: 'hidden'}}>
                            {actionButtons.map(btn => (
                                <AgyleButton
                                    key={btn.name}
                                    startIcon={btn?.icon}
                                    onClick={btn?.event}
                                    disabled={btn?.disabled}
                                    variant="outlined"
                                >
                                    {btn.display}
                                </AgyleButton>
                            ))}
                        </Grid>)
                    }
                </>
            }

            {!props.ipod && renderObservations()}

            <AgyleDialog
                open={showDefSusCan.open}
                width="sm"
                disableEscapeKeyDown={true}
                fullWidth
                noPadding
                closeButtonOnHeader
                onSave={changeNextObsStatus}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setShowDefSusCan({
                            open: false,
                            type: null,
                            lastObs: null,
                        });
                        setReason(null);
                    }
                }}
                title={getObservationActionTitle()}
            >
                <DialogContent className={classes.paperDarker}>
                    <Typography variant="h6" gutterBottom>
                        {t('Provide reason for this action')}
                    </Typography>

                    {showDefSusCan.type === 'SuspendNextObs' &&
                        <ObsSuspendReason
                            name="ObsSuspendReason"
                            label={t('Reason')}
                            color="tertiary"
                            value={reason}
                            agyleRefresh
                            onChange={(e) => setReason(e)}
                        />
                    }

                    {showDefSusCan.type !== 'SuspendNextObs' &&
                        <TextFieldDebounce
                            label="Reason"
                            error={reason === ''}
                            style={{width: '100%'}}
                            color={'info'}
                            agyleRefresh
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                        />
                    }
                </DialogContent>
            </AgyleDialog>

            <MessageBox
                onClose={() => {
                    setObs(null);
                    setConfirmCancel(false);
                }}
                title={t('Confirmation')}
                message={t('Are you sure you want to cancel this OBS entry?')}
                haveAccept={{yes: t('Yes'), no: t('No')}}
                onAccept={onConfirmCancel}
                open={confirmCancel}
            />
        </>
    );
}

export default Observations;
